import { useEffect, useMemo, useRef, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { v4 as uuidv4 } from "uuid";
import {
  OmsExportRequest,
  OmsProduct,
  OmsReport,
  Stage,
} from "../lusca-search-api/generated-src";
import { OmsReportsApiFactory } from "../lusca-search-api/LuscaSearchApiFactory";
import refreshOmsReportMetadata from "./omsReportMetadata";

type SavedExport = { jobId: string; report: OmsReport };

function readSavedExport(
  product: OmsProduct,
  report: OmsReport,
  mode: OmsExportRequest["mode"],
  contextKey: string
): SavedExport | null {
  try {
    const currentId = sessionStorage.getItem(
      `oms-export:${product}:${report.snapshotId}:${mode}`
    );
    const saved = JSON.parse(
      sessionStorage.getItem(contextKey) || "null"
    ) as SavedExport | null;
    if (
      saved &&
      typeof saved.jobId === "string" &&
      saved.report?.product === product &&
      typeof saved.report.snapshotId === "string" &&
      Number.isFinite(Date.parse(saved.report.sourceRefreshedAt)) &&
      Number.isFinite(saved.report.rowCount) &&
      Number.isFinite(saved.report.totalBytes) &&
      Array.isArray(saved.report.columns) &&
      saved.report.columns.every((column) => typeof column === "string") &&
      Array.isArray(saved.report.parts) &&
      (!currentId || currentId === saved.jobId)
    ) {
      return saved;
    }
    return currentId ? { jobId: currentId, report } : null;
  } catch {
    return null;
  }
}

export default function useOmsExport(
  product: OmsProduct,
  report: OmsReport,
  mode: OmsExportRequest["mode"],
  availableReport: OmsReport = report,
  resumeSaved = true
) {
  const contextKey = `oms-export-latest:${product}:${mode}`;
  const [saved] = useState(() =>
    resumeSaved ? readSavedExport(product, report, mode, contextKey) : null
  );
  const [pinnedReport, setSourceReport] = useState(saved?.report || report);
  const sourceReport = useMemo(
    () => refreshOmsReportMetadata(pinnedReport, availableReport),
    [pinnedReport, availableReport]
  );
  const { snapshotId } = sourceReport;
  const storageKey = `oms-export:${product}:${snapshotId}:${mode}`;
  const [jobId, setJobId] = useState<string | null>(saved?.jobId || null);
  const [resumeAvailable, setResumeAvailable] = useState(Boolean(saved));
  const [sent, setSent] = useState(false);
  const [deadlinePassed, setDeadlinePassed] = useState(false);
  const request = useRef<{ identity: string; id: string } | null>(null);
  function saveAcceptedExport(id: string): boolean {
    try {
      // Save before updating state: the dialog may close while POST is in flight.
      sessionStorage.setItem(
        contextKey,
        JSON.stringify({ jobId: id, report: sourceReport })
      );
      sessionStorage.setItem(storageKey, id);
      return true;
    } catch {
      return false;
    }
  }
  const create = useMutation({
    mutationFn: async (
      criteria: Pick<OmsExportRequest, "filters" | "operation">
    ) => {
      const body = {
        mode,
        filters: criteria.filters || [],
        operation: criteria.operation || "and",
      };
      const identity = JSON.stringify([product, snapshotId, body]);
      // A lost response must be safe to retry without changing an accepted job.
      if (request.current?.identity !== identity) {
        request.current = { identity, id: uuidv4() };
      }
      return (
        await OmsReportsApiFactory().createOmsExport(
          product,
          Stage.Prod,
          snapshotId,
          { requestId: request.current.id, ...body }
        )
      ).data;
    },
    onSuccess: (job) => {
      setResumeAvailable(saveAcceptedExport(job.jobId));
      setJobId(job.jobId);
      setSent(false);
    },
  });
  useEffect(() => {
    if (jobId) setResumeAvailable(saveAcceptedExport(jobId));
    // Persist the source schema together with the accepted job, including old IDs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId, storageKey, contextKey, sourceReport]);
  const job = useQuery({
    queryKey: ["oms-export", product, snapshotId, mode, jobId],
    enabled: Boolean(jobId),
    retry: false,
    queryFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExport(
          product,
          Stage.Prod,
          jobId as string
        )
      ).data,
    refetchInterval: (data, query) =>
      !query.state.error && (!data || data.status === "PREPARING")
        ? 3000
        : false,
    refetchIntervalInBackground: false,
  });
  const download = useMutation({
    mutationFn: async () =>
      (
        await OmsReportsApiFactory().getOmsExportDownload(
          product,
          Stage.Prod,
          jobId as string
        )
      ).data,
    onSuccess: (result) => {
      window.location.assign(result.url);
      setSent(true);
    },
  });
  const expiresAt = job.data?.expiresAt;
  useEffect(() => {
    const deadline = expiresAt ? new Date(expiresAt).getTime() : NaN;
    function checkDeadline() {
      setDeadlinePassed(Boolean(jobId) && Date.now() >= deadline);
    }
    checkDeadline();
    const timer = Number.isFinite(deadline)
      ? window.setTimeout(checkDeadline, Math.max(0, deadline - Date.now()))
      : undefined;
    window.addEventListener("focus", checkDeadline);
    document.addEventListener("visibilitychange", checkDeadline);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("focus", checkDeadline);
      document.removeEventListener("visibilitychange", checkDeadline);
    };
  }, [jobId, expiresAt]);
  function responseStatus(error: unknown): number | undefined {
    return (error as { response?: { status?: number } } | null)?.response
      ?.status;
  }
  const expired =
    deadlinePassed ||
    responseStatus(job.error) === 410 ||
    responseStatus(download.error) === 410;
  const unavailable =
    responseStatus(job.error) === 404 || responseStatus(download.error) === 404;
  const createConflict = responseStatus(create.error) === 409;
  function changeFilters() {
    request.current = null;
    setJobId(null);
    setSourceReport(availableReport);
    setResumeAvailable(false);
    setSent(false);
    setDeadlinePassed(false);
    create.reset();
    download.reset();
  }
  function reset() {
    try {
      sessionStorage.removeItem(storageKey);
      sessionStorage.removeItem(contextKey);
    } catch {
      // Reset still works when browser storage is disabled.
    }
    changeFilters();
  }
  return {
    create,
    job,
    download,
    jobId,
    sourceReport,
    resumeAvailable,
    sent,
    expired,
    unavailable,
    createConflict,
    changeFilters,
    reset,
  };
}
